import React, { useState, useEffect, useRef } from 'react';
import { Activity, TrendingUp, TrendingDown, History, Zap, Shield, ChevronRight, Timer, AlertCircle, Info, CheckCircle2 } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useTheme } from '@/shared/hooks/useTheme';
import { useUserAssets } from '@/shared/hooks/useUserAssets';
import { useTransactionManager } from '@/shared/hooks/useTransactionManager';
import { doc, onSnapshot, query, where, collection, orderBy, limit, setDoc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/shared/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import TradingSuspendedModal from '@/shared/components/TradingSuspendedModal';
import { updateDoc, serverTimestamp, increment, getDocs } from 'firebase/firestore';

// 시간 기반 라운드 계산 함수 (2분 간격, 하루 720회차)
const calculateCurrentRound = () => {
    const now = new Date();
    const totalMinutes = (now.getHours() * 60) + now.getMinutes();
    return Math.floor(totalMinutes / 2) + 1;
};

const getRoundId = (date: Date, roundNumber?: number) => {
    const targetDate = new Date(date);
    if (roundNumber !== undefined) {
        const currentR = Math.floor(((targetDate.getHours() * 60) + targetDate.getMinutes()) / 2) + 1;
        if (roundNumber > 600 && currentR < 100) {
            targetDate.setDate(targetDate.getDate() - 1);
        } else if (roundNumber < 100 && currentR > 600) {
            targetDate.setDate(targetDate.getDate() + 1);
        }
    }
    const yyyy = targetDate.getFullYear();
    const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
    const dd = String(targetDate.getDate()).padStart(2, '0');
    const rNum = roundNumber || calculateCurrentRound();
    return `${yyyy}-${mm}-${dd}-${rNum}`;
};

const getNextMinuteBoundary = () => {
    const now = new Date();
    const next = new Date(now);
    next.setSeconds(0);
    next.setMilliseconds(0);
    const currentMins = next.getMinutes();
    const remainder = currentMins % 2;
    next.setMinutes(currentMins + (2 - remainder));
    return next;
};

const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Loading...';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return date.toLocaleString();
};

const DayTradingPage: React.FC = () => {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { user, profile } = useAuth();
    const { assets, updateBalance } = useUserAssets(user?.uid);
    const { addTransaction } = useTransactionManager();

    const [round, setRound] = useState(0);
    const [roundId, setRoundId] = useState('');
    const [timeLeft, setTimeLeft] = useState(120);
    const [isBetting, setIsBetting] = useState(false);
    const [betAmount, setBetAmount] = useState('');
    const [winningMultiplier, setWinningMultiplier] = useState(1.95);
    const [maxBetAmount, setMaxBetAmount] = useState(10000000);
    const [resultOverride, setResultOverride] = useState('AUTO');
    const [isSettling, setIsSettling] = useState(false);
    const [recentResults, setRecentResults] = useState<any[]>([]);
    const [myResults, setMyResults] = useState<any[]>([]);
    // const [hasJoinedCurrentRound, setHasJoinedCurrentRound] = useState(false); // Refactored to useMemo
    const [queuedResult, setQueuedResult] = useState<any>(null);
    const [showResultModal, setShowResultModal] = useState(false);
    const [modalContent, setModalContent] = useState<{
        round: number;
        roundId: string;
        result: string;
        direction: 'UP' | 'DOWN' | null;
        userParticipated: boolean;
        userBetDirection: 'UP' | 'DOWN' | null;
        userBetAmount: number;
        userIsWin: boolean;
    } | null>(null);
    const [showTradingSuspendedModal, setShowTradingSuspendedModal] = useState(false);

    const endTimeRef = useRef<number>(0);

    // 결정론적 예측 함수 (관리자 페이지와 동일한 황금비 수열)
    const getPredictedResult = (r: number): 'UP' | 'DOWN' => {
        const frac = (r * 0.6180339887498949) % 1;
        return frac > 0.5 ? 'UP' : 'DOWN';
    };

    // Timer Ref to prevent closure staleness
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const lastTriggeredRoundRef = useRef<number>(0);
    const isSettleRequestedRef = useRef<number>(0);
    const lastSettledRoundRef = useRef<number>(0);

    // [REMOVED] autoSettleMyBets (Client-side settlement is deprecated)

    // Sync state with Firestore + Auto-Correction (admin page와 동일)
    useEffect(() => {
        const unsubscribe = onSnapshot(doc(db, 'system', 'day-trading'), async (snap: any) => {
            if (snap.exists()) {
                const data = snap.data();
                const firestoreRound = data.currentRound || 1;
                const targetRound = calculateCurrentRound();

                // ★ Auto-correction: Firestore 라운드가 뒤쳐져 있으면 즉시 업데이트 시도
                if (targetRound > firestoreRound && data.status === 'ACTIVE') {
                    const targetRoundId = getRoundId(new Date(), targetRound);
                    try {
                        await updateDoc(doc(db, 'system', 'day-trading'), {
                            currentRound: targetRound,
                            currentRoundId: targetRoundId,
                            endTime: getNextMinuteBoundary(),
                            lastUpdated: serverTimestamp()
                        });
                        console.log(`[DayTrading] Firestore 업데이트 성공: #${targetRoundId}`);
                    } catch (e) {
                        console.warn('[DayTrading] Firestore 업데이트 실패, 로컬 전환:', e);
                    }
                    // Firestore 업데이트 성공/실패 관계없이 로컬 상태는 즉시 전환
                    setRound(targetRound);
                    setRoundId(targetRoundId);
                    endTimeRef.current = getNextMinuteBoundary().getTime();
                } else {
                    // Firestore가 이미 최신이거나 보정 불필요한 경우에도 UI 상태 동기화 필수
                    setRound(firestoreRound);

                    // Resilience: If firestore currentRoundId is stale (doesn't match the round number), calculate it locally
                    const calculatedRoundId = getRoundId(new Date(), firestoreRound);
                    const firestoreRoundId = data.currentRoundId || calculatedRoundId;

                    if (firestoreRoundId.endsWith(`-${firestoreRound}`)) {
                        setRoundId(firestoreRoundId);
                    } else {
                        console.warn(`[DayTrading] Stale RoundId in Firestore detected: ${firestoreRoundId} for Round #${firestoreRound}. Using local calculation.`);
                        setRoundId(calculatedRoundId);
                    }

                    if (data.endTime) {
                        const end = typeof data.endTime.toDate === 'function' ? data.endTime.toDate().getTime() : new Date(data.endTime).getTime();
                        endTimeRef.current = end;
                    }
                }

                setWinningMultiplier(parseFloat(data.winningMultiplier || '1.95'));
                setMaxBetAmount(parseInt(data.maxBetAmount || '10000000'));
                setResultOverride(data.resultOverride || 'AUTO');
            }
        });
        return () => unsubscribe();
    }, []);

    // Check if user already joined the current round (Persistent & Derived from myResults)
    const hasJoinedCurrentRound = React.useMemo(() => {
        if (!user || !roundId || myResults.length === 0) return false;
        return myResults.some((tx: any) =>
            tx.metadata?.roundId === roundId &&
            tx.metadata?.type !== 'WIN'
        );
    }, [user, roundId, myResults]);

    // Fetch My Trade History (Persistent & Ordered)
    useEffect(() => {
        if (!user) {
            setMyResults([]);
            return;
        }

        // Increase limit to 50 for better persistence
        // Using both TRADING_BUY and TRADING_SELL to see full history
        const q = query(
            collection(db, 'transactions'),
            where('userId', '==', user.uid),
            where('type', 'in', ['TRADING_BUY', 'TRADING_SELL']),
            orderBy('timestamp', 'desc'),
            limit(50)
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            if (snap.empty) {
                console.log("No trading history found for user:", user.uid);
                // Don't clear if it was already populated? 
                // Actually, Firestore onSnapshot provides the current state.
                setMyResults([]);
                return;
            }
            const history = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMyResults(history);
        }, (err) => {
            console.error("Trade history fetch error:", err);
            // Fallback for missing index or other errors
            const fallbackQ = query(
                collection(db, 'transactions'),
                where('userId', '==', user.uid),
                limit(50)
            );
            onSnapshot(fallbackQ, (fSnap) => {
                const history = fSnap.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter((tx: any) => tx.type === 'TRADING_BUY' || tx.type === 'TRADING_SELL')
                    .sort((a: any, b: any) => {
                        const tA = a.timestamp?.seconds || 0;
                        const tB = b.timestamp?.seconds || 0;
                        return tB - tA;
                    });
                setMyResults(history);
            });
        });

        return () => unsubscribe();
    }, [user]);

    // Real-time Timer Logic
    useEffect(() => {
        timerRef.current = setInterval(() => {
            const now = new Date().getTime();
            const end = endTimeRef.current;
            if (end > 0) {
                const diff = Math.max(0, Math.ceil((end - now) / 1000));
                setTimeLeft(diff);
            }
        }, 1000);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [round]);

    // [액티브 트리거] 타이머 종료 즉시 정산 실행
    useEffect(() => {
        if (timeLeft === 0 && round > 0 && isSettleRequestedRef.current !== round) {
            isSettleRequestedRef.current = round;
            setIsSettling(true);

            console.log(`[DayTrading] 라운드 #${round} 종료. 정산 시작.`);

            // Cloud Function 호출 (백업)
            fetch('https://us-central1-coin2cashwts.cloudfunctions.net/requestInstantSettle')
                .then(res => res.json())
                .then(data => console.log(`[DayTrading] 서버 정산 응답:`, data))
                .catch(err => console.warn('[DayTrading] 서버 정산 요청 실패 (무시):', err));

            // 클라이언트 자체 정산 제거. 서버 정산 결과 대기.
            setTimeout(() => setIsSettling(false), 5000);
        }
    }, [timeLeft, round]);

    // ★ 페이지 로드 시 과거 미정산 베팅 일괄 정산
    useEffect(() => {
        if (!user || round <= 0 || myResults.length === 0) return;

        const unsettledPastBets = myResults.filter((tx: any) => {
            const txRound = Number(tx.metadata?.round);
            return txRound > 0 && txRound < round && tx.settled !== true && !tx.outcome;
        });

        if (unsettledPastBets.length === 0) return;

        // 미정산 라운드 목록 추출
        const unsettledRounds = [...new Set(unsettledPastBets.map((tx: any) => Number(tx.metadata?.round)))];
        console.log(`[DayTrading] 미정산 과거 라운드 ${unsettledRounds.length}개 발견:`, unsettledRounds);

        // 제거됨: 과거 베팅은 시스템(Cloud Functions)에서 자동으로 처리됩니다.
    }, [user, round, myResults.length]);

    // ★ 주기적 라운드 자동 동기화 (5초마다, 실패해도 로컬 상태는 즉시 전환)
    useEffect(() => {
        const syncInterval = setInterval(async () => {
            const targetRound = calculateCurrentRound();
            if (targetRound > round && round > 0) {
                const targetEndTime = getNextMinuteBoundary();
                console.log(`[DayTrading] 주기적 동기화: #${round} → #${targetRound}`);

                // 1. 로컬 상태 즉시 전환 (UI가 절대 멈추지 않음)
                setRound(targetRound);
                setRoundId(getRoundId(new Date(), targetRound));
                endTimeRef.current = targetEndTime.getTime();
                setIsSettling(false);

                // 2. Firestore도 업데이트 시도 (실패해도 OK)
                try {
                    await updateDoc(doc(db, 'system', 'day-trading'), {
                        currentRound: targetRound,
                        currentRoundId: getRoundId(new Date(), targetRound),
                        endTime: targetEndTime,
                        lastUpdated: serverTimestamp()
                    });
                    console.log(`[DayTrading] Firestore 동기화 성공`);
                } catch (err) {
                    console.warn('[DayTrading] Firestore 동기화 실패 (로컬은 이미 전환됨):', err);
                }
            }
        }, 5000);
        return () => clearInterval(syncInterval);
    }, [round]);


    // Result Checking Logic (Subscription)
    useEffect(() => {
        let unsubscribeBet: any = null;
        const q = query(
            collection(db, 'round_results'),
            orderBy('round', 'desc'),
            limit(1)
        );

        unsubscribeBet = onSnapshot(q, (snap) => {
            if (!snap.empty) {
                const resultData = snap.docs[0].data();
                setQueuedResult(resultData);
            }
        });

        return () => {
            if (unsubscribeBet) unsubscribeBet();
        };
    }, []);


    // TRIGGER MODAL ONLY WHEN RESULT IS CONFIRMED
    useEffect(() => {
        const triggerSettlement = async () => {
            // Show modal when round_results has a new entry matching the previous round (round just advanced)
            // queuedResult is the latest round_results entry
            const settledRound = queuedResult?.round;
            if (!settledRound || settledRound <= 0) return;
            if (lastTriggeredRoundRef.current === settledRound) return;

            // Only show if round just ended (current round is settledRound+1 or settledRound with timeLeft=0)
            const roundJustEnded = (round === settledRound + 1) || (round === settledRound && timeLeft === 0);
            if (!roundJustEnded) return;

            lastTriggeredRoundRef.current = settledRound;

            const resultTextKorean = queuedResult.result === 'UP' ? '매수' : '매도';

            // Find user's bet for the settled round
            const userBet = myResults.find((tx: any) =>
                tx.metadata?.round === settledRound &&
                (tx.type === 'TRADING_BUY' || tx.type === 'TRADING_SELL') &&
                tx.metadata?.type !== 'WIN'
            );
            const userParticipated = !!userBet;
            const userBetDirection: 'UP' | 'DOWN' | null = userBet
                ? (userBet.metadata?.direction || (userBet.type === 'TRADING_BUY' ? 'UP' : 'DOWN'))
                : null;
            const userBetAmount = userBet ? (parseInt(userBet.amount) || 0) : 0;
            const userIsWin = userParticipated && userBetDirection === queuedResult.result;

            setModalContent({
                round: settledRound,
                roundId: queuedResult.roundId || getRoundId(new Date(), settledRound),
                result: resultTextKorean,
                direction: queuedResult.result,
                userParticipated,
                userBetDirection,
                userBetAmount,
                userIsWin,
            });
            setShowResultModal(true);
        };

        triggerSettlement();
    }, [timeLeft, round, queuedResult, myResults]);


    // Fetch Recent Results - Synchronized with current round
    useEffect(() => {
        if (round <= 0) return;

        // Fetch results strictly less than the current active round
        const q = query(
            collection(db, 'round_results'),
            where('roundId', '<', roundId), // String comparison works for YYYY-MM-DD-NNN
            orderBy('roundId', 'desc'),
            limit(10)
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            const results = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Firestore query with orderBy handles sorting
            setRecentResults(results);
        }, (error) => {
            console.error("Error fetching results:", error);
            // Fallback: If index is missing or error occurs, try simple query
            const fallbackQ = query(
                collection(db, 'round_results'),
                orderBy('round', 'desc'),
                limit(20)
            );
            onSnapshot(fallbackQ, (fallbackSnap) => {
                const results = fallbackSnap.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter((r: any) => Number(r.round) < Number(round))
                    .sort((a: any, b: any) => Number(b.round) - Number(a.round))
                    .slice(0, 10);
                setRecentResults(results);
            });
        });
        return () => unsubscribe();
    }, [round]);

    // TradingView Widget Script
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://s3.tradingview.com/tv.js';
        script.async = true;
        script.onload = () => {
            if ((window as any).TradingView) {
                new (window as any).TradingView.widget({
                    "width": "100%",
                    "height": "100%",
                    "symbol": "BINANCE:BTCUSDT",
                    "interval": "1",
                    "timezone": "Etc/UTC",
                    "theme": "dark",
                    "style": "1",
                    "locale": "en",
                    "toolbar_bg": "#f1f3f6",
                    "enable_publishing": false,
                    "hide_side_toolbar": false,
                    "allow_symbol_change": true,
                    "container_id": "tradingview_widget"
                });
            }
        };
        const widgetContainer = document.getElementById('tradingview_widget');
        if (widgetContainer) {
            widgetContainer.appendChild(script);
        }

        return () => {
            const container = document.getElementById('tradingview_widget');
            if (container && script.parentNode === container) {
                container.removeChild(script);
            }
        };
    }, []);

    const handleBet = async (direction: 'UP' | 'DOWN') => {
        if (!user) {
            alert('로그인이 필요합니다.');
            return;
        }
        const amount = parseInt(betAmount.replace(/[^0-9]/g, ''));
        const FEE_RATE = 0.0005;

        if (isNaN(amount) || amount <= 0) {
            alert('금액을 입력해주세요');
            return;
        }

        const potentialFee = Math.floor(amount * FEE_RATE);
        const totalKrwRequired = amount + potentialFee;

        // DB-side verification to handle stale state/delays
        try {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (userDoc.exists()) {
                const userData = userDoc.data();

                // 🚨 TRADING SUSPENSION CHECK 🚨
                if (userData.tradingSuspended === true) {
                    setShowTradingSuspendedModal(true);
                    return;
                }

                const currentKrw = typeof userData.krw === 'string'
                    ? parseInt(userData.krw.replace(/,/g, ''))
                    : (userData.krw || 0);

                if (currentKrw < totalKrwRequired) {
                    alert(`보유하신 원화(KRW) 잔액이 부족합니다.\n필요 금액: ₩${totalKrwRequired.toLocaleString()}\n현재 잔액: ₩${currentKrw.toLocaleString()}`);
                    return;
                }
            }
        } catch (error) {
            console.error("Balance verification failed:", error);
            // Fallback to local assets state if DB fetch fails
            if ((assets?.krw || 0) < totalKrwRequired) {
                alert('보유하신 원화(KRW) 잔액이 부족합니다.');
                return;
            }
        }

        if (timeLeft < 10) {
            alert('라운드 종료 임박! 다음 라운드에 참여해주세요.');
            return;
        }

        // 🔒 DB-level duplicate bet check (prevents race condition bypass)
        try {
            const { getDocs, query: fsQuery, collection: fsCollection, where: fsWhere } = await import('firebase/firestore');
            const dupCheck = await getDocs(fsQuery(
                fsCollection(db, 'transactions'),
                fsWhere('userId', '==', user.uid),
                fsWhere('metadata.roundId', '==', roundId)
            ));
            if (!dupCheck.empty) {
                alert('이미 이번 회차에 체결된 주문이 있습니다.');
                return;
            }
        } catch (e) {
            // fallback to local state check
            if (hasJoinedCurrentRound) {
                alert('이미 참여하신 베팅이 있습니다.');
                return;
            }
        }

        if (hasJoinedCurrentRound) {
            alert('이미 참여하신 베팅이 있습니다.');
            return;
        }

        // REQUIRED DEDUCTION LOGIC
        setIsBetting(true);
        try {
            console.log(`[DayTrading] Requesting deduction for Round #${round}: ${totalKrwRequired} KRW`);

            // 1. Mandatory Balance Deduction FIRST (or right after TX)
            // To ensure "돈이 안 줄어드는" problem is solved, we await it.
            await updateBalance('krw', -totalKrwRequired);
            console.log(`[DayTrading] Deduction Successful. Creating transaction record...`);

            // 2. Add Transaction record
            const txId = await addTransaction({
                userId: user.uid,
                userName: user.displayName || user.email?.split('@')[0] || 'User',
                type: direction === 'UP' ? 'TRADING_BUY' : 'TRADING_SELL',
                amount: amount,
                asset: 'KRW',
                status: 'COMPLETED',
                settled: false,
                metadata: {
                    description: `Day Trading ${direction} (Round #${roundId}) - Fee: ${potentialFee}`,
                    round: Number(round),
                    roundId: roundId,
                    direction: direction,
                }
            });

            if (txId) {
                setBetAmount('');
                alert('주문이 체결되었습니다.');
            } else {
                // Critical: if TX creation fails after deduction, we should ideally rollback or log heavily.
                console.error("[DayTrading] CRITICAL: Deduction succeeded but TX creation failed.");
                alert('주문 처리 중 오류가 발생했습니다. 고객센터에 문의해주세요.');
            }
        } catch (error) {
            console.error("[DayTrading] Bet Failure:", error);
            alert('주문 실패: ' + (error as any).message);
        }
        setIsBetting(false);
    };

    return (
        <div className={`min-h-screen bg-[#0a0a0c] text-white font-sans selection:bg-blue-500/30 ${theme === 'dark' ? 'dark' : ''}`}>
            <div className="container mx-auto p-3 md:p-4 lg:p-6 max-w-[1600px] space-y-4 md:space-y-6">

                {/* Header */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-4">
                    <div className="flex items-center gap-2 md:gap-3">
                        <div className="p-2 md:p-3 rounded-xl md:rounded-2xl bg-blue-600 shadow-lg shadow-blue-600/20">
                            <Activity className="h-5 w-5 md:h-6 md:w-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl md:text-2xl lg:text-3xl font-black text-white italic tracking-tighter">Day Trading [v3.1]</h1>
                            <p className="text-[10px] md:text-xs text-slate-500 font-bold uppercase tracking-widest">BTC 1분봉 (2분거래)</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-3 md:gap-4 lg:gap-6">
                    {/* Left Column Wrapper - Mobile: Contents (Direct Grid Items), Desktop: Block (Column) */}
                    <div className="contents lg:block lg:col-span-9 lg:space-y-6">
                        {/* Chart Section - Mobile Order 1 */}
                        <div className="col-span-12 order-1 lg:order-none space-y-3 md:space-y-4 lg:space-y-6">
                            <div className="relative group overflow-hidden rounded-2xl md:rounded-[2rem] bg-[#0a0a0c] border border-white/5 p-1 shadow-2xl h-[300px] md:h-[400px] lg:h-[500px]">
                                <div className="relative bg-[#09090b] rounded-[1.8rem] overflow-hidden h-full">
                                    <div id="tradingview_widget" className="h-full w-full" />
                                </div>
                            </div>
                        </div>

                        {/* My Trade History Section - Mobile Order 3 */}
                        <div className="col-span-12 order-3 lg:order-none font-sans">
                            <div className="rounded-2xl md:rounded-[2.5rem] bg-[#0a0a0c] border border-white/5 p-4 md:p-6 shadow-2xl space-y-3 md:space-y-4">
                                <h3 className="text-[11px] md:text-[12px] font-black text-slate-500 uppercase tracking-widest pl-1 md:pl-2 flex items-center gap-2">
                                    <Zap className="h-3 w-3 md:h-3.5 md:w-3.5 text-blue-500" />
                                    My Trading History
                                </h3>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="border-b border-white/5 text-[10px] text-slate-500 uppercase font-black">
                                                <th className="pb-3 pl-2">Round</th>
                                                <th className="pb-3 text-center">Direction</th>
                                                <th className="pb-3 text-right pr-2">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/5 text-xs">
                                            {myResults.length === 0 ? (
                                                <tr>
                                                    <td colSpan={3} className="py-12 text-center text-slate-600 italic">No trading records found</td>
                                                </tr>
                                            ) : (
                                                (() => {
                                                    // Group transactions by round to show one row per participation
                                                    const grouped = myResults.reduce((acc: any, item: any) => {
                                                        const date = item.timestamp?.toDate ? item.timestamp.toDate() : new Date(item.timestamp?.seconds * 1000 || Date.now());
                                                        const dateKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
                                                        const round = item.metadata?.round || 0;
                                                        const groupKey = `${dateKey}-${round}`;

                                                        if (!round) return acc;

                                                        const rawAmtStr = String(item.amount || '0').replace(/[^0-9]/g, '');
                                                        const amt = parseInt(rawAmtStr) || 0;
                                                        const isBetTx = item.type === 'TRADING_BUY' || item.type === 'TRADING_SELL';
                                                        const isPayoutTx = item.metadata?.type === 'WIN';

                                                        if (!acc[groupKey]) {
                                                            acc[groupKey] = {
                                                                round: round,
                                                                type: item.type,
                                                                dir: item.metadata?.direction || (item.type === 'TRADING_BUY' ? 'UP' : 'DOWN'),
                                                                timestamp: item.timestamp,
                                                                amount: isBetTx ? amt : 0,
                                                                isWin: isPayoutTx || item.outcome === 'WIN',
                                                                settled: item.settled === true || isPayoutTx,
                                                                outcome: isPayoutTx ? 'WIN' : item.outcome,
                                                                payoutAmount: isPayoutTx ? amt : 0
                                                            };
                                                        } else {
                                                            // If we find the actual bet transaction, use its amount
                                                            if (isBetTx) {
                                                                acc[groupKey].amount = amt;
                                                                if (item.metadata?.direction) acc[groupKey].dir = item.metadata.direction;
                                                            }

                                                            // If we find a payout transaction, mark as win and set payout amount
                                                            if (isPayoutTx) {
                                                                acc[groupKey].isWin = true;
                                                                acc[groupKey].settled = true;
                                                                acc[groupKey].outcome = 'WIN';
                                                                acc[groupKey].payoutAmount = amt;
                                                            }
                                                            if (item.metadata?.direction) acc[groupKey].dir = item.metadata.direction;
                                                        }
                                                        return acc;
                                                    }, {});

                                                    return Object.values(grouped)
                                                        .sort((a: any, b: any) => {
                                                            const tA = a.timestamp?.seconds || 0;
                                                            const tB = b.timestamp?.seconds || 0;
                                                            return tB - tA;
                                                        })
                                                        .map((item: any, idx) => {
                                                            const isSettled = item.settled === true;
                                                            const hasOutcome = item.outcome === 'WIN' || item.outcome === 'LOSS';
                                                            const isWin = item.isWin === true || item.outcome === 'WIN';
                                                            const betDirection = item.dir;
                                                            const betAmount = item.amount;
                                                            const itemRound = item.round;
                                                            const isPastRound = Number(itemRound) < Number(round);

                                                            // ✅ 과거 라운드면 무조건 정산 완료로 표시
                                                            const displaySettled = isSettled || isPastRound;
                                                            // ✅ outcome 필드가 있으면 무조건 그것을 사용 (관리자 수동 결과와 일치)
                                                            let displayWin = isWin;
                                                            if (!hasOutcome && isPastRound && !isSettled) {
                                                                // outcome이 없는 과거 라운드: 결정론적 예측 사용
                                                                const predictedResult = getPredictedResult(Number(itemRound));
                                                                displayWin = betDirection === predictedResult;
                                                            }
                                                            return (
                                                                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className={cn(
                                                                            "w-10 h-10 rounded-full flex items-center justify-center",
                                                                            !displaySettled ? "bg-yellow-500/20 text-yellow-500" :
                                                                                (displayWin ? "bg-emerald-500/20 text-emerald-500" : "bg-rose-500/20 text-rose-500")
                                                                        )}>
                                                                            {!displaySettled ? <Timer className="w-5 h-5" /> : (betDirection === 'UP' ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />)}
                                                                        </div>
                                                                        <div>
                                                                            <div className="flex items-center gap-2">
                                                                                <span className="text-sm font-medium text-white">#{item.round}</span>
                                                                                <span className={cn(
                                                                                    "text-xs px-2 py-0.5 rounded-full font-medium",
                                                                                    !displaySettled ? "bg-yellow-500/20 text-yellow-500" :
                                                                                        (displayWin ? "bg-emerald-500/20 text-emerald-500" : "bg-rose-500/20 text-rose-500")
                                                                                )}>
                                                                                    {!displaySettled ? '대기 중' : (displayWin ? '✅ 수익실현' : '❌ 수익미실현')}
                                                                                </span>
                                                                            </div>
                                                                            <div className="text-[10px] text-white/40 mt-0.5">
                                                                                {formatDate(item.timestamp)}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <div className="text-sm md:text-base font-bold text-white">
                                                                            ₩{betAmount.toLocaleString()}
                                                                        </div>
                                                                        {displaySettled && displayWin && (
                                                                            <div className="text-[10px] text-emerald-400 font-bold">
                                                                                +₩{(item.payoutAmount || Math.round(betAmount * winningMultiplier)).toLocaleString()} 지급
                                                                            </div>
                                                                        )}
                                                                        <div className={cn(
                                                                            "text-[10px] font-medium uppercase",
                                                                            betDirection === 'UP' ? "text-emerald-500" : "text-rose-500"
                                                                        )}>
                                                                            {betDirection === 'UP' ? '매수(UP)' : '매도(DOWN)'}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        });
                                                })()
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column Wrapper - Mobile: Contents (Direct Grid Items), Desktop: Block (Column) */}
                    <div className="contents lg:block lg:col-span-3 lg:space-y-6">
                        {/* Inner Right Column (Betting Area) - Mobile Order 2 */}
                        <div className="col-span-12 order-2 lg:order-none space-y-3 md:space-y-4 lg:space-y-6">
                            {/* Timer Card */}
                            <div className="rounded-2xl md:rounded-[2.5rem] bg-indigo-600/10 border border-indigo-500/20 p-4 md:p-6 flex flex-col items-center justify-center gap-1 md:gap-2 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/20 blur-[60px] rounded-full" />
                                <span className="text-[11px] md:text-[12px] font-black text-indigo-300 uppercase tracking-widest relative z-10 flex items-center gap-1.5 md:gap-2">
                                    <Activity className="h-3 w-3" />
                                    {roundId} 회차
                                </span>
                                <div className={cn(
                                    "text-3xl md:text-4xl font-black font-mono tabular-nums relative z-10 drop-shadow-lg",
                                    timeLeft === 0 ? "text-rose-400 animate-pulse" : "text-indigo-400"
                                )}>
                                    {timeLeft === 0 ? "SETTLING..." :
                                        `${String(Math.floor(timeLeft / 60)).padStart(2, '0')}:${String(timeLeft % 60).padStart(2, '0')}`
                                    }
                                </div>
                                {isSettling && (
                                    <div className="text-[9px] font-bold text-indigo-300/60 animate-bounce mt-1">
                                        시스템이 정산을 처리하고 있습니다...
                                    </div>
                                )}
                            </div>



                            {/* Betting Panel */}
                            <div className="rounded-2xl md:rounded-[2.5rem] bg-[#0a0a0c] border border-white/5 p-4 md:p-6 shadow-2xl space-y-4 md:space-y-6 relative overflow-hidden">
                                <div className="space-y-3 md:space-y-4">
                                    <label className="text-[11px] md:text-xs font-bold text-slate-500 uppercase tracking-widest pl-1 md:pl-2">Trade Amount</label>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            value={betAmount}
                                            onChange={(e) => setBetAmount(e.target.value.replace(/[^0-9]/g, ''))}
                                            placeholder="0"
                                            className="w-full h-14 md:h-16 bg-slate-900 border border-white/5 rounded-xl md:rounded-2xl px-4 md:px-6 text-right text-xl md:text-2xl font-black text-white focus:outline-none focus:border-blue-500/50 transition-all font-mono shadow-inner"
                                        />
                                        <span className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 text-xs md:text-sm font-black text-slate-500 italic">KRW</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3 md:gap-4">
                                    <button
                                        onClick={() => handleBet('UP')}
                                        disabled={isBetting || hasJoinedCurrentRound}
                                        className={cn(
                                            "h-28 md:h-32 lg:h-36 rounded-2xl md:rounded-3xl flex flex-col items-center justify-center gap-1.5 md:gap-2 transition-all shadow-xl group relative overflow-hidden",
                                            "bg-emerald-500 hover:bg-emerald-400 active:scale-95 disabled:opacity-50 disabled:grayscale",
                                            (isBetting || hasJoinedCurrentRound) && "cursor-not-allowed opacity-50"
                                        )}
                                    >
                                        <TrendingUp className="h-7 w-7 md:h-8 md:w-8 text-white drop-shadow-md" />
                                        <div className="text-center">
                                            <div className="text-xl md:text-2xl lg:text-3xl font-black text-white tracking-tight drop-shadow-sm">매수</div>
                                            <div className="text-[8px] md:text-[9px] font-bold text-emerald-100 uppercase tracking-wider mt-0.5 opacity-70">BUY</div>
                                            <div className="text-[9px] md:text-[10px] font-bold text-emerald-100 uppercase tracking-widest mt-0.5 md:mt-1">x{winningMultiplier.toFixed(2)}</div>
                                        </div>
                                        {hasJoinedCurrentRound && <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-[9px] md:text-[10px] font-black uppercase tracking-widest">체결완료</div>}
                                    </button>

                                    <button
                                        onClick={() => handleBet('DOWN')}
                                        disabled={isBetting || hasJoinedCurrentRound}
                                        className={cn(
                                            "h-28 md:h-32 lg:h-36 rounded-2xl md:rounded-3xl flex flex-col items-center justify-center gap-1.5 md:gap-2 transition-all shadow-xl group relative overflow-hidden",
                                            "bg-rose-500 hover:bg-rose-400 active:scale-95 disabled:opacity-50 disabled:grayscale",
                                            (isBetting || hasJoinedCurrentRound) && "cursor-not-allowed opacity-50"
                                        )}
                                    >
                                        <TrendingDown className="h-7 w-7 md:h-8 md:w-8 text-white drop-shadow-md" />
                                        <div className="text-center">
                                            <div className="text-xl md:text-2xl lg:text-3xl font-black text-white tracking-tight drop-shadow-sm">매도</div>
                                            <div className="text-[8px] md:text-[9px] font-bold text-rose-100 uppercase tracking-wider mt-0.5 opacity-70">SELL</div>
                                            <div className="text-[9px] md:text-[10px] font-bold text-rose-100 uppercase tracking-widest mt-0.5 md:mt-1">x{winningMultiplier.toFixed(2)}</div>
                                        </div>
                                        {hasJoinedCurrentRound && <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-[9px] md:text-[10px] font-black uppercase tracking-widest">체결완료</div>}
                                    </button>
                                </div>
                            </div>

                            {/* Recent Results List */}
                            <div className="rounded-2xl md:rounded-[2.5rem] bg-[#0a0a0c] border border-white/5 p-4 md:p-6 shadow-2xl space-y-3 md:space-y-4">
                                <h3 className="text-[11px] md:text-[12px] font-black text-slate-500 uppercase tracking-widest pl-1 md:pl-2 flex items-center gap-1.5 md:gap-2">
                                    <History className="h-3 w-3 md:h-3.5 md:w-3.5" />
                                    Recent History
                                </h3>
                                <div className="space-y-1.5 md:space-y-2">
                                    {recentResults.map((res: any, idx: number) => (
                                        <div key={idx} className="flex items-center justify-between p-2.5 md:p-3 rounded-xl md:rounded-2xl bg-white/5 border border-white/5">
                                            <span className="text-[9px] md:text-[10px] font-bold text-slate-500 tracking-wider">#{res.round}</span>
                                            <span className={cn(
                                                "text-[11px] md:text-xs font-black italic tracking-tighter",
                                                res.result === 'UP' ? "text-emerald-500" : "text-rose-500"
                                            )}>
                                                {res.result === 'UP' ? '매수' : '매도'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Round Result Modal */}
            {showResultModal && modalContent && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/70 backdrop-blur-md animate-in fade-in duration-300">
                    <div className={cn(
                        "w-full max-w-md rounded-3xl p-0 shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden border",
                        modalContent.userParticipated
                            ? (modalContent.userIsWin
                                ? "bg-emerald-950 border-emerald-500/40"
                                : "bg-red-950 border-red-500/30")
                            : "bg-slate-900 border-white/10"
                    )}>
                        {/* Top accent bar */}
                        <div className={cn(
                            "h-1.5 w-full",
                            modalContent.userParticipated
                                ? (modalContent.userIsWin ? "bg-emerald-400" : "bg-red-500")
                                : (modalContent.direction === 'UP' ? "bg-emerald-500" : "bg-rose-500")
                        )} />

                        <div className="p-8 space-y-5">
                            {/* Header */}
                            <div className="text-center">
                                <p className="text-xs font-bold text-white/40 uppercase tracking-widest">#{modalContent.roundId || modalContent.round} 회차 결과</p>
                                <div className={cn(
                                    "mt-3 text-5xl font-black tracking-tight",
                                    modalContent.direction === 'UP' ? "text-emerald-400" : "text-rose-400"
                                )}>
                                    {modalContent.result}
                                </div>
                                <p className="text-xs text-white/30 mt-1">
                                    {modalContent.direction === 'UP' ? 'BTC 상승 마감' : 'BTC 하락 마감'}
                                </p>
                            </div>

                            {/* Personal result section */}
                            {modalContent.userParticipated ? (
                                <div className={cn(
                                    "rounded-2xl p-4 text-center space-y-2",
                                    modalContent.userIsWin ? "bg-emerald-500/20" : "bg-red-500/10"
                                )}>
                                    <div className={cn(
                                        "text-2xl font-black",
                                        modalContent.userIsWin ? "text-emerald-300" : "text-red-400"
                                    )}>
                                        {modalContent.userIsWin ? '🎉 수익실현!' : '❌ 수익미실현'}
                                    </div>
                                    <div className="flex justify-between text-sm text-white/60 pt-1">
                                        <span>투자 방향</span>
                                        <span className={cn(
                                            "font-bold",
                                            modalContent.userBetDirection === 'UP' ? "text-emerald-400" : "text-rose-400"
                                        )}>
                                            {modalContent.userBetDirection === 'UP' ? '매수 (UP)' : '매도 (DOWN)'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm text-white/60">
                                        <span>투자 금액</span>
                                        <span className="font-bold text-white">₩{modalContent.userBetAmount.toLocaleString()}</span>
                                    </div>
                                    {modalContent.userIsWin && (
                                        <div className="flex justify-between text-sm border-t border-emerald-500/30 pt-2 mt-1">
                                            <span className="text-emerald-300 font-bold">수익 지급</span>
                                            <span className="font-black text-emerald-300">+₩{Math.floor(modalContent.userBetAmount * winningMultiplier).toLocaleString()}</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="rounded-2xl p-4 text-center bg-white/5 text-white/40 text-sm">
                                    이 회차에 참여하지 않았습니다.
                                </div>
                            )}

                            {/* Action button */}
                            <button
                                onClick={() => setShowResultModal(false)}
                                className={cn(
                                    "w-full py-4 rounded-2xl font-black tracking-widest text-sm transition-all active:scale-95",
                                    modalContent.userParticipated && modalContent.userIsWin
                                        ? "bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/30"
                                        : "bg-white/10 hover:bg-white/15 text-white"
                                )}
                            >
                                다음 회차 →
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 거래정지 모달 */}
            {showTradingSuspendedModal && (
                <TradingSuspendedModal onClose={() => setShowTradingSuspendedModal(false)} />
            )}
        </div>
    );
};

export default DayTradingPage;
